import { Box } from '@mui/material'
import styled from '@emotion/styled/macro'
import theme from 'theme'
import { DevicesTableContainer } from 'ui/pages/cpi-details/sections/devices/devices.styles'

export const ModalRoot = styled(Box)`
  display: flex;
  flex-direction: column;
  background: ${theme.colors.surfaceContainerBackground};
  border-radius: ${theme.attributes.borderRadius.quaternary};
  width: 100%;
  height: 100%;
  padding: ${theme.spacing.large}px;
  overflow: hidden;
  box-sizing: border-box;

  .reason {
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.half}px;
    padding-bottom: ${theme.spacing.medium}px;
    font-family: ${theme.typography.fontFamily.primary};
  }

  .reason-label {
    color: ${theme.colors.devicesSecondaryText};
    font-family: inherit;
    font-size: ${theme.typography.fontSize.medium};
    font-weight: ${theme.typography.fontWeight.normal};
    line-height: 18px;
  }

  .reason-value {
    color: ${theme.colors.menuItemText};
    font-family: inherit;
    font-size: ${theme.typography.fontSize.large};
    font-weight: ${theme.typography.fontWeight.bold};
    line-height: 20px;
  }
`

export const Header = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing.small}px;
  padding-bottom: ${theme.spacing.medium}px;

  .title {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: ${theme.colors.menuItemText};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.xxxLarge};
    font-weight: ${theme.typography.fontWeight.bold};
    line-height: 32px;
  }

  .header-actions {
    display: flex;
    align-items: center;
    gap: ${theme.spacing.half}px;
    flex-shrink: 0;
  }

  .edit-button {
    color: ${theme.colors.menuItemText};

    &:hover {
      color: ${theme.colors.cta};
    }
  }

  html[data-theme='light'] & .MuiIconButton-root svg path {
    fill: ${theme.colors.menuItemText};
  }

  html[data-theme='light'] & .edit-button svg path {
    fill: none;
  }
`

export const MetaRow = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing.large}px;
  padding-bottom: ${theme.spacing.medium}px;

  .meta-tile {
    display: flex;
    align-items: center;
    gap: ${theme.spacing.md}px;
    min-width: 0;
  }

  .meta-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    border-radius: ${theme.attributes.borderRadius.secondary};
    background: ${theme.colors.surfaceItemBackground};
    color: ${theme.colors.menuItemText};
  }

  .meta-label {
    display: flex;
    align-items: center;
    gap: ${theme.spacing.half}px;
    color: ${theme.colors.devicesSecondaryText};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.medium};
    font-weight: ${theme.typography.fontWeight.normal};
    line-height: 18px;
  }

  .meta-hint {
    display: flex;
    cursor: help;

    svg {
      width: 14px;
      height: 14px;
    }

    svg path {
      fill: ${theme.colors.devicesSecondaryText};
    }
  }

  .meta-value {
    color: ${theme.colors.menuItemText};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.large};
    font-weight: ${theme.typography.fontWeight.bold};
    line-height: 20px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`

export const InfoBar = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: ${theme.spacing.small}px;

  .records-count.MuiTypography-root {
    color: ${theme.colors.menuItemText};
    font-size: ${theme.typography.fontSize.xLarge};
    font-weight: ${theme.typography.fontWeight.bold};
    line-height: 24px;
  }

  .MuiTypography-root {
    color: ${theme.colors.softMauve};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.large};
    font-weight: ${theme.typography.fontWeight.normal};

    span {
      color: ${theme.colors.neutral};
    }

    html[data-theme='light'] &,
    html[data-theme='light'] & span {
      color: #5c5865;
    }
  }
`

export const RecordsTableWrapper = styled(DevicesTableContainer)`
  /* Override the OV container's full-viewport height/padding — the modal owns sizing now. */
  height: 100%;
  padding: 0;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
  min-width: 0;

  /* OV hides filter UI until JS adds a 'filter-has-values' class (async value lookup).
     In the modal, set-filter values come from the in-memory records, so we always show them. */
  .ag-popup .ag-mini-filter,
  .ag-popup .ag-virtual-list-viewport,
  .ag-popup .ag-filter-apply-panel {
    visibility: visible !important;
  }

  /* OV reserves 12px right margin for its custom vertical scrollbar — modal doesn't use it. */
  .ag-root-wrapper .ag-root {
    margin-right: 0 !important;
  }

  /* Override the global pink scrollbar (index.css) for the table's horizontal scroll. */
  .ag-body-horizontal-scroll {
    height: 12px !important;
    max-height: 12px !important;
    min-height: 12px !important;

    .ag-body-horizontal-scroll-viewport {
      &::-webkit-scrollbar {
        height: 12px !important;
        background: transparent !important;
      }

      &::-webkit-scrollbar-track {
        background: transparent !important;
        box-shadow: none !important;
      }

      &::-webkit-scrollbar-thumb {
        background-color: #372e35 !important;
        border-radius: ${theme.attributes.borderRadius.quaternary} !important;
      }
    }
  }

  .grid-host {
    flex: 1 1 auto;
    min-height: 0;
    min-width: 0;
    width: 100%;
  }

  .empty,
  .loading {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 200px;
    color: ${theme.colors.dropdownLabel};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.large};
  }
`
